import { logger } from "@/utils/logger";
import React, { useState } from "react";

import { Camera, ChevronDown, ChevronUp, Shield, Sparkles, Info, Plus, Minus, Copy, Eye } from "lucide-react";

import { VehicleListItem } from "@/utils/messageParser/types";

import { useLanguage } from "@/contexts/LanguageContext";

import { Badge } from "@/components/ui/badge";

import { Button } from "@/components/ui/button";

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";

import { useFormContext } from "@/contexts/FormContext";

import { useCentralizedVehicleInterest } from "@/hooks/useCentralizedVehicleInterest";

import { useIsMobile } from "@/hooks/use-mobile";


import SecureStorage from "@/utils/secureStorage";

import { ga4Tracker, extractVehicleData } from "@/utils/ga4Tracking";

import { toast } from "sonner";

import { openURL } from "@/utils/navigationUtils";

import { VehiclePaymentModal } from "./VehiclePaymentModal";

import { useDealerSession } from "@/contexts/DealerSessionContext";

import PaymentSelector from "./PaymentSelector";

import { PaymentPreferencesProvider, usePaymentPreferences } from "@/contexts/PaymentPreferencesContext";
import { buildOfferFormTitle } from "@/utils/vehicleContextHelpers";

interface InventoryCardsProps {
  vehicles: VehicleListItem[];
}

const InventoryCards: React.FC<InventoryCardsProps> = ({ vehicles }) => {
  const { language, t } = useLanguage();

  const isMobile = useIsMobile();

  const {
    activateOfferForm,
    activateCarfaxForm,
    updateFormData,
    setActiveForm,
    setIsOfferHighlighted,
    formRef,
    aiFormData,
  } = useFormContext();

  const { storeVehicleInterest } = useCentralizedVehicleInterest();

  const { sessionData } = useDealerSession();

  const [expandedCards, setExpandedCards] = useState<Set<number>>(new Set());

  const [paymentModalOpen, setPaymentModalOpen] = useState(false);

  const [selectedVehiclePayments, setSelectedVehiclePayments] = useState<any[]>([]);

  const [selectedVehicleInfo, setSelectedVehicleInfo] = useState<any>(null);

  const [loadingPayments, setLoadingPayments] = useState(false);

  // Debug logging
  logger.log("🔍 InventoryCards Debug:", {
    sessionDataExists: !!sessionData,
    paymentsEnabled: sessionData?.paymentsEnabled,
    vehiclesCount: vehicles.length,
    dealerId: sessionData?.dealerId,
  });

  // We render PaymentSelector directly per-card (it fetches its own data once sessionData.domain is ready).
  // This avoids timing/race issues where a prefetch map isn't populated after streaming.


  const formatPrice = (price: number | undefined) => {
    if (!price) return language === "fr" ? "Prix non disponible" : "Price not available";

    if (language === "fr") {
      // French format: xxx xxx $

      return `${price.toLocaleString("fr-CA")} $`;
    } else {
      // English format: $xxx,xxx

      return `$${price.toLocaleString("en-CA")}`;
    }
  };

  const formatMileage = (mileage: number | undefined) => {
    if (!mileage && mileage !== 0) return "N/A";

    return `${mileage.toLocaleString("fr-CA")} km`;
  };

  const getDisplayModel = (vehicle: VehicleListItem) => {
    if (!vehicle.Model) return "";

    // VehicleListItem doesn't have Trim property, so just return Model

    return vehicle.Model;
  };

  const getPriceDisclaimer = () => {
    return localStorage.getItem("Price_Disclaimer") || "";
  };

  const getLocalizedVdpUrl = (url: string): string => {
    if (language === 'fr') {
      let localized = url;
      localized = localized.replace(/\/new\/inventory\//gi, '/neufs/inventaire/');
      localized = localized.replace(/\/used\//gi, '/occasion/');
      localized = localized.replace(/\/new\//gi, '/neuf/');
      return localized;
    }
    return url;
  };

  const handleImageClick = async (vehicle: VehicleListItem) => {
    // Store vehicle as interest when image is clicked

    storeVehicleAsInterest(vehicle);

    if (vehicle.VDP_URL) {
      await openURL(getLocalizedVdpUrl(vehicle.VDP_URL));
    }
  };

  const replaceNotSpecified = (value: string | undefined) => {
    if (!value || value === "Not specified") return "";

    return value;
  };

  const copyToClipboard = async (text: string, type: "vin" | "stock") => {
    try {
      await navigator.clipboard.writeText(text);

      const message =
        type === "vin"
          ? language === "fr"
            ? "NIV copié dans le presse-papiers!"
            : "VIN Copied to clipboard"
          : language === "fr"
            ? "Stock copié dans le presse-papiers!"
            : "Stock Copied to clipboard";

      toast.success(message, {
        duration: 1500,
      });
    } catch (err) {
      console.error("Failed to copy: ", err);

      toast.error(language === "fr" ? "Échec de la copie" : "Copy failed", {
        duration: 1500,
      });
    }
  };

  const toggleCardExpansion = (index: number) => {
    const newExpanded = new Set(expandedCards);

    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }

    setExpandedCards(newExpanded);
  };

  const storeVehicleAsInterest = (vehicle: VehicleListItem) => {
    const interestData = {
      vehicle: {
        make: vehicle.Make || "",

        model: vehicle.Model || "",

        version: "", // List view doesn't have trim, that's correct

        year: vehicle.Year?.toString() || "",

        color: vehicle.Color || "",

        vin: vehicle.VIN || "",

        stock: vehicle.Stock_Number || "",

        main_picture: vehicle.Main_Picture || "",

        price: vehicle.Price || undefined,

        mileage: vehicle.Mileage || undefined,

        exterior_color: vehicle.Color || "",

        vdp_url: vehicle.VDP_URL || "",

        features: vehicle.Features || [],

        description: vehicle.Vehicle_Description_Summary || "",

        fuel_type: vehicle.Fuel_Type || "",
      },
    };

    storeVehicleInterest(vehicle);
  };

  const handleCarfaxClick = async (vehicle: VehicleListItem) => {
    // DIRECT STORAGE - BYPASS ALL OTHER FUNCTIONS

    const directInterestData = {
      vehicle: {
        make: vehicle.Make || "",

        model: vehicle.Model || "",

        version: "", // List view doesn't have trim

        year: vehicle.Year?.toString() || "",

        color: vehicle.Color || "",

        vin: vehicle.VIN || "",

        stock: vehicle.Stock_Number || "",

        main_picture: vehicle.Main_Picture || "",

        price: vehicle.Price || undefined,

        mileage: vehicle.Mileage || undefined,

        exterior_color: vehicle.Color || "",

        vdp_url: vehicle.VDP_URL || "",

        features: vehicle.Features || [],

        description: vehicle.Vehicle_Description_Summary || "",

        fuel_type: vehicle.Fuel_Type || "",
      },

      timestamp: new Date().toISOString(),

      chatId: sessionStorage.getItem("chatbot_chat_id") || "default-chat-id",
    };

    // Store using SecureStorage

    await SecureStorage.setItem("chatbot_interest_data", directInterestData);

    // Verify immediately

    const verification = await SecureStorage.getItem("chatbot_interest_data");

    // Parse and verify the content

    try {
      const parsed = verification || {};
    } catch (error) {
      console.error("🔍 DIRECT STORAGE - Parse error:", error);
    }

    // Analytics tracking moved to FormContext to prevent duplicates

    // Pass the VIN to the Carfax form activation

    activateCarfaxForm(vehicle.VIN);
  };

  const handleSeeOfferClick = (vehicle: VehicleListItem) => {
    storeVehicleAsInterest(vehicle);

    // Analytics tracking moved to FormContext to prevent duplicates

    // Only create a custom title if there's no AI-provided title already

    // This prevents overriding the AI's explicit Form.Title from JSON responses

    const aiData: any = {
      Type: "Offers",

      form: "Offer",
    };

    // Only add FormTitle if there's no existing AI form data with a Title

    if (!aiFormData?.Title) {
      const offerTitle = buildOfferFormTitle(language === 'fr' ? 'fr' : 'en', {
        year: vehicle.Year,
        make: vehicle.Make,
        model: vehicle.Model,
        trim: (vehicle as any).Trim,
      });

      aiData.FormTitle = offerTitle;
      aiData.Title = offerTitle;
    }

    // Activate offer form

    setActiveForm("Offer", aiData);

    setIsOfferHighlighted(true);

    // Expand form if it's collapsed

    if (formRef?.current) {
      formRef.current.expand();
    }
  };

  const shouldShowCarfax = (vehicle: VehicleListItem) => {
    if (vehicle.Mileage === undefined || vehicle.Mileage === null) return true;

    // Handle both string and number mileage (mileage can actually come as a string from API)

    let numericValue: number;

    const mileage = vehicle.Mileage as number | string;

    if (typeof mileage === "string") {
      // Extract numeric value from string like "15 km" or "91,350 km"

      numericValue = parseFloat(mileage.replace(/[^\d.]/g, ""));
    } else {
      // Handle numeric mileage

      numericValue = mileage;
    }

    return numericValue >= 200;
  };

  const translateFuelType = (fuelType: string) => {
    if (language === "fr") {
      // French translations

      if (fuelType.toLowerCase().includes("gas")) {
        return "Gaz";
      }

      if (
        fuelType.toLowerCase().includes("hybrid") &&
        !fuelType.toLowerCase().includes("phev") &&
        !fuelType.toLowerCase().includes("plugin") &&
        !fuelType.toLowerCase().includes("rechargeable")
      ) {
        return "Hybride";
      }
    }

    return fuelType;
  };

  const handlePaymentBadgeClick = async (vehicle: VehicleListItem) => {
    if (!vehicle.VIN) {
      toast.error(language === "fr" ? "NIV non disponible" : "VIN not available");

      return;
    }

    setLoadingPayments(true);

    setSelectedVehicleInfo({
      year: vehicle.Year,

      make: vehicle.Make,

      model: vehicle.Model,

      trim: "",
    });

    try {
      const dealerBaseUrl = sessionData?.domain
        ? `https://${sessionData.domain.replace(/^https?:\/\//, "")}`
        : window.location.origin;

      const apiUrl = `${dealerBaseUrl}/ajax/getPaymentForVehicleVIN?VIN=${vehicle.VIN}`;

      const response = await fetch(apiUrl, {
        headers: { Accept: "application/json" },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Transform the response object into an array of payment options

      const payments: any[] = [];

      // Add finance options

      if (data.finance) {
        Object.entries(data.finance).forEach(([key, value]: [string, any]) => {
          payments.push({
            term: value.term,

            monthly_payment: parseFloat(value.payment.monthly.replace(/,/g, "")),

            apr: parseFloat(value.rate),

            down_payment: 0,

            type: "finance",
          });
        });
      }

      // Add lease options

      if (data.lease) {
        Object.entries(data.lease).forEach(([key, value]: [string, any]) => {
          payments.push({
            term: value.term,

            monthly_payment: parseFloat(value.payment.monthly.replace(/,/g, "")),

            apr: parseFloat(value.rate),

            down_payment: 0,

            type: "lease",
          });
        });
      }

      if (payments.length > 0) {
        setSelectedVehiclePayments(payments);

        setPaymentModalOpen(true);
      } else {
        toast.error(language === "fr" ? "Aucune donnée de paiement disponible" : "No payment data available");
      }
    } catch (error) {
      toast.error(language === "fr" ? "Erreur lors du chargement des paiements" : "Error loading payments");
    } finally {
      setLoadingPayments(false);
    }
  };

  return (
    <PaymentPreferencesProvider>
      <div className="w-full max-w-full space-y-3 mb-6 overflow-hidden">
        {/* In-stock models banner */}

        <div className="flex items-center justify-center h-10 bg-white rounded-md">
          <span className="text-sm font-medium text-gray-700">
            {t(vehicles.length === 1 ? "inventory.modelInInventory" : "inventory.modelsInInventory")}
          </span>
        </div>

        {vehicles.map((vehicle, index) => {
          const isExpanded = expandedCards.has(index);

          const hasFeatures = vehicle.Features && vehicle.Features.length > 0;

          const hasDescription = vehicle.Vehicle_Description_Summary;

          const hasStockOrVin = vehicle.Stock_Number || vehicle.VIN;

          const hasExpandableContent = hasFeatures || hasDescription || hasStockOrVin;

          return (
            <div
              key={index}
              className="w-full max-w-full bg-white rounded-lg d2cbot-bot_border d2cbot-bot_border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden relative"
            >
              {/* Payment Banner - Only show for NEW vehicles (or vehicles without status) */}
              {(() => {
                if (!sessionData?.paymentsEnabled || !vehicle.VIN || (vehicle.Status && vehicle.Status.toLowerCase() !== "new")) {
                  return null;
                }

                return (
                  <PaymentSelector
                    vin={vehicle.VIN}
                    initialPaymentType="Lease"
                    initialTerm={36}
                    initialKmAllowance={20000}
                    frequency={sessionData?.paymentFrequency || "monthly"}
                  />
                );
              })()}

              {/* Main vehicle info row */}

              <div
                className={`flex items-center mb-3 p-4 pb-0 ${(() => {
                  if (!sessionData?.paymentsEnabled || !vehicle.VIN || (vehicle.Status && vehicle.Status.toLowerCase() !== "new")) {
                    return "";
                  }

                  // Banner is rendered above, so add spacing below it.
                  return "mt-4";
                })()}`}
              >
                {/* Column 1: Vehicle Image */}

                <div className="flex-shrink-0 w-16 mr-4">
                  <div className="relative">
                    {vehicle.Main_Picture ? (
                      <>
                        <HoverCard>
                          <HoverCardTrigger asChild>
                            <img
                              src={
                                vehicle.Main_Picture.startsWith("http")
                                  ? vehicle.Main_Picture
                                  : `https://${vehicle.Main_Picture}`
                              }
                              alt={`${vehicle.Year} ${vehicle.Make} ${vehicle.Model}`}
                              className={`w-16 h-16 object-cover rounded-lg d2cbot-bot_border d2cbot-bot_border-gray-200 ${vehicle.VDP_URL ? "cursor-pointer hover:opacity-80 transition-opacity" : ""}`}
                              onClick={() => handleImageClick(vehicle)}
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;

                                const hasRetried = target.getAttribute("data-retry");

                                if (!hasRetried) {
                                  target.setAttribute("data-retry", "true");

                                  setTimeout(() => {
                                    const originalSrc = target.src;

                                    target.src = "";

                                    target.src = originalSrc;
                                  }, 2000);
                                } else {
                                  // Hide the image and its hover card wrapper

                                  const hoverCard = target.closest("[data-radix-hover-card-trigger]");

                                  if (hoverCard) {
                                    (hoverCard as HTMLElement).style.display = "none";
                                  } else {
                                    target.style.display = "none";
                                  }

                                  // Show the placeholder (it's a sibling of the HoverCard in the parent container)

                                  const container = target.closest(".relative");

                                  const placeholder = container?.querySelector(".bg-gradient-to-br") as HTMLElement;

                                  if (placeholder) {
                                    placeholder.classList.remove("hidden");

                                    placeholder.classList.add("flex");
                                  }
                                }
                              }}
                            />
                          </HoverCardTrigger>

                          <HoverCardContent
                            className="w-auto p-0 d2cbot-bot_border-0 bg-transparent shadow-2xl"
                            side="top"
                            align="start"
                            sideOffset={-50}
                            alignOffset={-10}
                          >
                            <div className="bg-white rounded-lg d2cbot-bot_border d2cbot-bot_border-gray-300 shadow-lg overflow-hidden">
                              {/* Header with VIN only */}

                              {vehicle.VIN && (
                                <div className="bg-gray-50 px-3 py-2 d2cbot-bot_border-b d2cbot-bot_border-gray-200">
                                  <div className="flex items-center justify-center gap-2">
                                    <span className="text-xs font-mono text-gray-600">
                                      {language === "fr" ? "NIV" : "VIN"}: {vehicle.VIN}
                                    </span>

                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();

                                        copyToClipboard(vehicle.VIN!, "vin");
                                      }}
                                      className="text-gray-500 hover:text-gray-700 transition-colors"
                                      title="Copy VIN"
                                    >
                                      <Copy className="w-3 h-3" />
                                    </button>
                                  </div>
                                </div>
                              )}

                              {/* Image with padding */}

                              <div className="p-3 flex justify-center relative">
                                <img
                                  src={
                                    vehicle.Main_Picture.startsWith("http")
                                      ? vehicle.Main_Picture
                                      : `https://${vehicle.Main_Picture}`
                                  }
                                  alt={`${vehicle.Year} ${vehicle.Make} ${vehicle.Model}`}
                                  className={`w-48 h-36 object-cover rounded d2cbot-bot_border d2cbot-bot_border-gray-300 ${vehicle.VDP_URL ? "cursor-pointer hover:opacity-80 transition-opacity" : ""}`}
                                  onClick={() => handleImageClick(vehicle)}
                                  onError={(e) => {
                                    const target = e.target as HTMLImageElement;

                                    const hasRetried = target.getAttribute("data-retry");

                                    if (!hasRetried) {
                                      target.setAttribute("data-retry", "true");

                                      setTimeout(() => {
                                        const originalSrc = target.src;

                                        target.src = "";

                                        target.src = originalSrc;
                                      }, 2000);
                                    } else {
                                      target.style.display = "none";

                                      // Show fallback in hover card

                                      const container = target.parentElement;

                                      if (container) {
                                        const fallback = document.createElement("div");

                                        fallback.className =
                                          "w-48 h-36 bg-gradient-to-br from-gray-50 to-gray-100 rounded d2cbot-bot_border d2cbot-bot_border-gray-300 flex items-center justify-center text-gray-400";

                                        fallback.innerHTML = `

                                        <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">

                                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"/>

                                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"/>

                                        </svg>

                                      `;

                                        container.appendChild(fallback);
                                      }
                                    }
                                  }}
                                />
                              </div>
                            </div>
                          </HoverCardContent>
                        </HoverCard>

                        <div
                          className="w-16 h-16 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg d2cbot-bot_border d2cbot-bot_border-gray-200 items-center justify-center text-gray-400 hidden"
                          onClick={() => handleImageClick(vehicle)}
                        >
                          <Camera className="w-4 h-4" />
                        </div>
                      </>
                    ) : (
                      <div
                        className={`w-16 h-16 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg d2cbot-bot_border d2cbot-bot_border-gray-200 flex items-center justify-center text-gray-400 ${vehicle.VDP_URL ? "cursor-pointer hover:opacity-80 transition-opacity" : ""}`}
                        onClick={() => handleImageClick(vehicle)}
                      >
                        <Camera className="w-4 h-4" />
                      </div>
                    )}
                  </div>
                </div>

                {/* Column 2: Year, Make, Model, Trim */}

                <div className={`flex-grow ${isMobile ? "min-w-[38%]" : "min-w-0"}`}>
                  <div className="text-xs font-medium text-gray-500 mb-0.5">{vehicle.Year}</div>

                  <div className="text-sm font-bold text-gray-900 leading-tight">{vehicle.Make}</div>

                  {isMobile ? (
                    <div className="text-sm text-gray-700 font-medium truncate" title={getDisplayModel(vehicle)}>
                      {getDisplayModel(vehicle)}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-700 font-medium">{getDisplayModel(vehicle)}</div>
                  )}
                  
                  {vehicle.Trim && (
                    <div className="text-xs text-gray-500 truncate" title={vehicle.Trim}>
                      {vehicle.Trim}
                    </div>
                  )}
                </div>

                {/* Column 3: Fuel Type + Color badges, Mileage, Price */}

                <div className={`text-right shrink-0 ${isMobile ? "max-w-[35%]" : "max-w-[45%]"}`}>
                  <div className="flex flex-wrap items-center justify-end gap-1 mb-0.5">
                    {vehicle.Fuel_Type && (
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          borderRadius: "9999px",
                          fontSize: "0.75rem",
                          fontWeight: "600",
                          padding: "0.125rem 0.625rem",
                          border: "1px solid",
                          whiteSpace: "nowrap",
                          overflowWrap: "normal",
                          wordBreak: "keep-all",
                          ...(vehicle.Fuel_Type.toLowerCase().includes("electric") ||
                          vehicle.Fuel_Type.toLowerCase().includes("électrique")
                            ? { backgroundColor: "#dcfce7", color: "#166534", borderColor: "#86efac" }
                            : vehicle.Fuel_Type.toLowerCase().includes("phev") ||
                                vehicle.Fuel_Type.toLowerCase().includes("plugin") ||
                                vehicle.Fuel_Type.toLowerCase().includes("rechargeable")
                              ? { backgroundColor: "#dbeafe", color: "#1e40af", borderColor: "#93c5fd" }
                              : vehicle.Fuel_Type.toLowerCase().includes("hybrid") ||
                                  vehicle.Fuel_Type.toLowerCase().includes("hybride")
                                ? { backgroundColor: "#f1f5f9", color: "#64748b", borderColor: "#cbd5e1" }
                                : { backgroundColor: "#f1f5f9", color: "#64748b", borderColor: "#cbd5e1" })
                        }}
                      >
                        {vehicle.Fuel_Type.toLowerCase().includes("plugin") ||
                        vehicle.Fuel_Type.toLowerCase().includes("rechargeable") ||
                        vehicle.Fuel_Type.toLowerCase().includes("phev")
                          ? "PHEV"
                          : translateFuelType(vehicle.Fuel_Type)}
                      </span>
                    )}

                    {vehicle.Color && vehicle.Color !== "Not specified" && (
                      <span
                        style={{
                          display: "inline-flex",

                          alignItems: "center",

                          borderRadius: "9999px",

                          fontSize: "0.75rem",

                          fontWeight: "600",

                          padding: "0.125rem 0.625rem",

                          backgroundColor: "#f3f4f6",

                          color: "#4b5563",

                          border: "1px solid #e5e7eb",

                          whiteSpace: "nowrap",

                          overflowWrap: "normal",

                          wordBreak: "keep-all",
                        }}
                      >
                        {vehicle.Color.charAt(0).toUpperCase() + vehicle.Color.slice(1).toLowerCase()}
                      </span>
                    )}
                  </div>

                  <div className="text-xs text-gray-500 mb-0.5 whitespace-nowrap">{formatMileage(vehicle.Mileage)}</div>

                  <div className="flex flex-col items-end gap-1">
                    <div className="text-sm font-bold text-black flex items-center justify-end gap-1 whitespace-nowrap">
                      {formatPrice(vehicle.Price)}

                      {getPriceDisclaimer() && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Info className="w-3 h-3 cursor-help text-gray-500" />
                            </TooltipTrigger>

                            <TooltipContent>
                              <p>{getPriceDisclaimer()}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Action buttons row - always show regardless of features */}

              <div className="space-y-2 px-4 pb-4">
                <div className="flex flex-wrap gap-y-2 justify-between items-center">
                  {/* More/Less button - show if vehicle has any expandable content */}

                  {hasExpandableContent ? (
                    <button
                      onClick={() => toggleCardExpansion(index)}
                      className="d2cbot-btn-auto flex items-center gap-1 text-xs text-gray-600 hover:text-gray-800 transition-colors"
                    >
                      {isExpanded ? (
                        <>
                          <Minus className="w-3 h-3" />

                          {language === "fr" ? "Moins" : "Less"}
                        </>
                      ) : (
                        <>
                          <Plus className="w-3 h-3" />

                          {language === "fr" ? "Plus" : "More"}
                        </>
                      )}
                    </button>
                  ) : (
                    <div></div> // Empty div to maintain flex layout
                  )}

                  {/* Action buttons on the right side - always show */}

                  <div className="flex flex-wrap justify-end gap-1.5 gap-y-2">
                    {shouldShowCarfax(vehicle) && (
                      <Button
                        onClick={() => handleCarfaxClick(vehicle)}
                        variant="outline"
                        size="sm"
                        className="!bg-black d2cbot-bot_border-black !text-white hover:!bg-gray-700 hover:!text-white px-1.5 py-0.5 text-xs rounded-md transition-colors duration-200 h-7 flex items-center gap-1"
                      >
                        <Shield className="w-2.5 h-2.5" />

                        {t("vehicle.carfax")}
                      </Button>
                    )}

                    <Button
                      onClick={async () => {
                        storeVehicleAsInterest(vehicle);

                        if (vehicle.VDP_URL) {
                          await openURL(getLocalizedVdpUrl(vehicle.VDP_URL));
                        }
                      }}
                      variant="outline"
                      size="sm"
                      className="!bg-black d2cbot-bot_border-black !text-white hover:!bg-gray-700 hover:!text-white px-1.5 py-0.5 text-xs rounded-md transition-colors duration-200 h-7 flex items-center gap-1"
                    >
                      <Eye className="w-2.5 h-2.5" />

                      {language === "fr" ? "Voir" : "View"}
                    </Button>

                    <Button
                      onClick={() => handleSeeOfferClick(vehicle)}
                      size="sm"
                      className="!bg-red-600 hover:!bg-red-700 text-white hover:!text-white px-1.5 py-0.5 text-xs rounded-md transition-colors duration-200 h-7 flex items-center gap-1"
                    >
                      <Sparkles className="w-2.5 h-2.5" />

                      {language === "fr" ? "Voir l'offre" : "See offer"}
                    </Button>
                  </div>
                </div>

                {/* Expanded content - show when expanded AND vehicle has expandable content */}

                {hasExpandableContent && isExpanded && (
                  <div className="space-y-3 pt-2 d2cbot-bot_border-t d2cbot-bot_border-gray-100">
                    {/* Stock # and VIN fields - Simple left/right aligned layout */}

                    {hasStockOrVin && (
                      <div className="space-y-1 mb-3">
                        {vehicle.Stock_Number && (
                          <div className="flex justify-between items-center">
                            <span className="text-xs font-medium text-gray-700">Stock #:</span>

                            <div className="flex items-center gap-2">
                              <span className="text-xs text-gray-600">{vehicle.Stock_Number}</span>

                              <button
                                onClick={(e) => {
                                  e.stopPropagation();

                                  copyToClipboard(vehicle.Stock_Number!, "stock");
                                }}
                                className="text-gray-500 hover:text-gray-700 transition-colors"
                                title="Copy Stock #"
                              >
                                <Copy className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                        )}

                        {vehicle.VIN && (
                          <div className="flex justify-between items-center">
                            <span className="text-xs font-medium text-gray-700">
                              {language === "fr" ? "NIV:" : "VIN:"}
                            </span>

                            <div className="flex items-center gap-2">
                              <span className="text-xs text-gray-600 font-mono">{vehicle.VIN}</span>

                              <button
                                onClick={(e) => {
                                  e.stopPropagation();

                                  copyToClipboard(vehicle.VIN!, "vin");
                                }}
                                className="text-gray-500 hover:text-gray-700 transition-colors"
                                title="Copy VIN"
                              >
                                <Copy className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Vehicle Location */}

                    {vehicle["vehicle location"] && (
                      <div className="mb-3 pb-3 border-b border-gray-200">
                        <h4 className="text-sm font-semibold text-gray-900 mb-2">{t("vehicle.location")}</h4>

                        <div className="space-y-1">
                          {vehicle["vehicle location"].name && (
                            <div className="text-xs font-medium text-gray-900">{vehicle["vehicle location"].name}</div>
                          )}

                          {vehicle["vehicle location"].location && (
                            <div className="text-xs text-gray-600">
                              {vehicle["vehicle location"].location.city &&
                                `${vehicle["vehicle location"].location.city}, `}

                              {vehicle["vehicle location"].location.province &&
                                `${vehicle["vehicle location"].location.province} `}

                              {vehicle["vehicle location"].location.postalCode}
                            </div>
                          )}

                          {vehicle["vehicle location"].phone && (
                            <div className="text-xs text-gray-600">{vehicle["vehicle location"].phone}</div>
                          )}
                        </div>
                      </div>
                    )}

                    {hasDescription && (
                      <div className="mb-3">
                        <h4 className="text-sm font-semibold text-gray-900 mb-2">Description</h4>

                        <p className="text-sm text-gray-600 leading-relaxed bg-gray-50 p-3 rounded-lg">
                          {vehicle.Vehicle_Description_Summary}
                        </p>
                      </div>
                    )}

                    {hasFeatures && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900 mb-2">Highlights</h4>

                        <div className="flex flex-wrap gap-2">
                          {vehicle.Features!.map((feature, featureIndex) => (
                            <span
                              key={featureIndex}
                              style={{
                                display: "inline-flex",

                                alignItems: "center",

                                borderRadius: "9999px",

                                fontSize: "0.75rem",

                                fontWeight: "600",

                                padding: "0.25rem 0.75rem",

                                backgroundColor: "#eff6ff",

                                color: "#1e40af",

                                border: "1px solid #bfdbfe",
                              }}
                            >
                              {feature}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {/* Payment Modal */}

        <VehiclePaymentModal
          open={paymentModalOpen}
          onOpenChange={(open) => setPaymentModalOpen(open)}
          vehicleInfo={selectedVehicleInfo}
          payments={selectedVehiclePayments}
        />
      </div>
    </PaymentPreferencesProvider>
  );
};

export default InventoryCards;
